import { MARK_C_PATH, MARK_C_STROKE, MARK_ICON_LAYERS, MARK_ICON_VIEWBOX } from "../lib/mark";

/* The mark and the word as ONE word — the phone's `BrandLockup`, ported.
 *
 * 🔴 THE MARK IS EMITTED, NOT DRAWN. `lib/mark.ts` is a GENERATED file: its paths are computed
 * from the three constants `KataMark.swift` declares, by the same emitter that writes the
 * palette. `CLAUDE.md` names this exact remedy — *"redrawing it by eye would be the second
 * statement of the brand … port it by emitting it from `MarkGeometry`"* — because a hand-drawn
 * `<path>` here would be a second brand to keep in step with the first, which is this repo's
 * defining bug class with an SVG in the place of a vocabulary.
 *
 * ── THE THREE THINGS THAT MAKE IT READ AS ONE WORD ─────────────────────────────────────────
 *
 * The phone's own header lists them, and they are the reason this is a component rather than an
 * `<img>` beside some text:
 *
 *   SIZE      the plate is one em across, so the disc stands as tall as the h and the k beside
 *             it. The C alone at ascender height reads as a dot with a pinprick in it.
 *   BASELINE  the disc sits ON the baseline with a hair of overshoot, the way every round
 *             letter does. Centre-aligning the two boxes floats it above the word.
 *   SPACING   the gap is measured from the disc's INK. The plate OVERFLOWS the mark's own
 *             frame on every side, so spacing against the box leaves a hole — which is exactly
 *             what made the phone's earlier lockup read as a logo parked next to a word.
 *
 * ⚠️ THE WORD IS "oachwork", AND THAT IS NOT A TYPO. `Brand.markIsFirstLetter` is true on the
 * phone: the mark stands in for the leading letter, so the lockup spells the name once rather
 * than twice. The stroke is a bowl with an opening facing east — it has a C's shape already,
 * and nothing was redrawn to make it fit.
 *
 * ⚠️ AND IT IS `aria-label`-ED AS THE WHOLE NAME. A screen reader must hear "coachwork", never
 * "oachwork" — the mark carrying a letter is a visual fact, and leaving it to the alt text is
 * how a wordmark becomes unreadable to everybody not looking at it.
 */

/**
 * The plate itself — the APP ICON's rings, not the flat disc.
 *
 * 🔴 THE OWNER ASKED FOR THIS BY LOOKING AT IT (2026-08-30): *"why we still use the old app
 * icon/logo for the web app? use the latest one that we use for our app too."* He was right and
 * the difference was real: this drew `KataMark`'s flat form — one red disc with a large bore
 * knocked straight through — while his home screen shows a competition plate with a rim, a
 * recessed groove, a machined steel hub and a small bore punched from that hub.
 *
 * ⚠️ THE RINGS ARE EMITTED FROM `design/AppIcon/generate.py`, THE FILE THAT DRAWS THE ICON, and
 * every one of them names a CSS TOKEN rather than a colour. Both halves matter: hand-copying
 * `RIM_R = 38.6` here would be a second statement of the icon, and a hex here would be a second
 * statement of the brand's red. `mark.ts`'s header carries the rest of the reasoning.
 *
 * ⚠️ THE BORE IS STILL A KNOCKOUT. The icon paints its bore with the ground it knows; a themed
 * page has no such constant, so the hole is punched from the HUB with `evenodd` and whatever the
 * page is standing on shows through — right in both appearances with nothing to keep in step.
 */
function PlateFace() {
  return (
    <>
      {MARK_ICON_LAYERS.map((layer) => (
        <path
          key={layer.name}
          d={layer.d}
          fill={`var(${layer.token})`}
          fillRule={layer.evenOdd ? "evenodd" : undefined}
          opacity={layer.opacity}
        />
      ))}
      {/* 🔴 STROKED, NOT FILLED — the same primitive `IconCShape` uses on the phone.
          `KataIconFace` draws a bare arc and lets SwiftUI stroke it with `lineCap: .round`, so
          `stroke-linecap="round"` here is not an approximation of the app's C, it is the app's
          C. The previous version emitted a filled polygon that approximated the two caps, and
          at wordmark size they rendered as thin spurs off the terminals — which is exactly what
          the owner saw when he put the two lockups side by side. */}
      <path
        d={MARK_C_PATH}
        fill="none"
        stroke="var(--ht-plate-creme)"
        strokeWidth={MARK_C_STROKE}
        strokeLinecap="round"
      />
    </>
  );
}

export function BrandLockup({ size = 22 }: { size?: number }) {
  return (
    <span className="brand-lockup" style={{ fontSize: `${size}px` }} aria-label="coachwork">
      <svg
        className="brand-mark"
        viewBox={MARK_ICON_VIEWBOX}
        // Decorative HERE and only here: the name is announced by the label on the parent, so
        // announcing the mark as well would read the word twice.
        aria-hidden="true"
        focusable="false"
      >
        <PlateFace />
      </svg>
      <span className="brand-word" aria-hidden="true">
        oachwork
      </span>
    </span>
  );
}

/**
 * The mark ALONE, with no word beside it.
 *
 * The rail's top, at the owner's direction: *"on top our logo, without the rest of the text of
 * our app name"*. It is the same two generated paths as the lockup — not a second drawing — so
 * the brand is still stated once. Collapsed, this is the only thing in the rail's header; locked
 * open, the lock button sits to its right.
 *
 * ⚠️ IT KEEPS THE FULL NAME AS ITS LABEL. The mark stands in for a letter, so a reader who cannot
 * see it must still hear "coachwork" — the same reason `BrandLockup` labels the pair rather than
 * letting the alt text spell "oachwork".
 */
export function BrandMark({ size = 24 }: { size?: number }) {
  return (
    <span className="brand-mark-only" style={{ fontSize: `${size}px` }} aria-label="coachwork">
      <svg className="brand-mark" viewBox={MARK_ICON_VIEWBOX} aria-hidden="true" focusable="false">
        <PlateFace />
      </svg>
    </span>
  );
}
